import { useQuery } from '@tanstack/react-query';

import React, { useMemo, useState } from 'react';

import { ExpandMore, Search } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, InputAdornment, Stack, TextField, Typography } from '@mui/material';

import { useApiClient } from '@@common';
import { CopyToClipboardButton } from '@@components';

import { ClientClient } from '../../../api/api';

interface RedirectUrlPlaceholderHintProps {
	clientId?: string;
	environmentId?: string;
}

const genericPlaceholders: PlaceholderDisplayModel[] = [
	{ name: 'Environment.Id', source: 'BuiltIn', description: 'Environment identifier' },
	{ name: 'Environment.Code', source: 'BuiltIn', description: 'Environment code' },
	{ name: 'Environment.Name', source: 'BuiltIn', description: 'Environment name' },
	{ name: 'Tenant.Id', source: 'BuiltIn', description: 'Tenant identifier' },
	{ name: 'Tenant.Code', source: 'BuiltIn', description: 'Tenant code' },
	{ name: 'Client.Id', source: 'BuiltIn', description: 'Client identifier' },
	{ name: 'Client.Name', source: 'BuiltIn', description: 'Client name' },
	{ name: 'Client.Code', source: 'BuiltIn', description: 'Client code' },
];

interface PlaceholderDisplayModel {
	name?: string;
	placeholder?: string;
	source?: string;
	description?: string;
}

export default function RedirectUrlPlaceholderHint({ clientId, environmentId }: RedirectUrlPlaceholderHintProps) {
	const client = useApiClient(ClientClient) as ClientClient;
	const [searchText, setSearchText] = useState('');
	const canLoadContextualPlaceholders = !!clientId && !!environmentId;

	const { data } = useQuery({
		queryKey: ['client-redirect-url-placeholders', clientId, environmentId],
		queryFn: () => client.getRedirectUrlPlaceholders(clientId!, environmentId!),
		enabled: canLoadContextualPlaceholders,
	});

	const placeholders: PlaceholderDisplayModel[] = useMemo(
		() => (canLoadContextualPlaceholders ? data?.placeholders ?? [] : genericPlaceholders),
		[canLoadContextualPlaceholders, data?.placeholders],
	);
	const filteredPlaceholders = useMemo(() => {
		const normalizedSearchText = searchText.trim().toLowerCase();
		if (!normalizedSearchText) {
			return placeholders;
		}

		return placeholders.filter((placeholder) => {
			const token = getPlaceholderToken(placeholder).toLowerCase();
			const searchableText = [token, placeholder.source, placeholder.description].filter(Boolean).join(' ').toLowerCase();
			return searchableText.includes(normalizedSearchText);
		});
	}, [placeholders, searchText]);

	return (
		<Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}>
			<AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
				<Typography variant="body2" color="textSecondary">
					Available placeholders
				</Typography>
			</AccordionSummary>
			<AccordionDetails sx={{ pt: 0 }}>
				<Stack spacing={1.25}>
					<TextField
						value={searchText}
						onChange={(event) => setSearchText(event.target.value)}
						placeholder="Search placeholders"
						size="small"
						fullWidth
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<Search sx={{ fontSize: 18 }} />
								</InputAdornment>
							),
						}}
					/>
					<Stack
						spacing={0.5}
						sx={{
							maxHeight: 220,
							overflowY: 'auto',
							pr: 0.5,
						}}
					>
						{filteredPlaceholders.map((placeholder) => {
							const token = getPlaceholderToken(placeholder);
							const source = placeholder.source;
							return (
								<Stack
									key={token}
									direction="row"
									spacing={1}
									alignItems="center"
									justifyContent="space-between"
									sx={{
										minHeight: 30,
										px: 0.75,
										py: 0.25,
										borderRadius: 0.75,
										bgcolor: 'action.hover',
									}}
								>
									<Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, flexShrink: 0 }}>
										<Typography
											variant="body2"
											sx={{
												fontFamily: 'monospace',
												fontWeight: 500,
											}}
										>
											{token}
										</Typography>
										<CopyToClipboardButton
											value={token}
											title={`Copy ${token}`}
											edge={false}
											iconSize={14}
											sx={{ p: 0.25, flexShrink: 0 }}
										/>
									</Stack>
									<Box sx={{ minWidth: 0, flexGrow: 1, textAlign: 'right' }}>
										<Typography variant="caption" color="textSecondary" noWrap>
											{[placeholder.description, source].filter(Boolean).join(' - ')}
										</Typography>
									</Box>
								</Stack>
							);
						})}
						{filteredPlaceholders.length === 0 && (
							<Typography variant="body2" color="textSecondary">
								No placeholders found.
							</Typography>
						)}
					</Stack>
					{!canLoadContextualPlaceholders && (
						<Typography variant="caption" color="textSecondary">
							Environment and client attributes appear here after the client is assigned to an environment.
						</Typography>
					)}
				</Stack>
			</AccordionDetails>
		</Accordion>
	);
}

function getPlaceholderToken(placeholder: PlaceholderDisplayModel): string {
	const name = placeholder.name ?? placeholder.placeholder ?? '';
	return name.startsWith('{') ? name : `{${name}}`;
}
